import { GridCellProps } from '@progress/kendo-react-grid';
import { cloneDeep } from 'lodash';
import moment from 'moment-timezone';
import { useCallback, useMemo } from 'react';
import GridCellBase from 'src/components/GridCellBase';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import Button from 'src/components/design-system/Button';
import PopUp from 'src/components/design-system/PopUp';
import useViewCalendarYear from 'src/hooks/useViewCalendarYear';

const DeletePeriod: React.FC<GridCellProps> = (props) => {
  const { dataItem } = props;
  const { rowId } = dataItem;
  const { setSelectedFYPeriod, selectedFYPeriod } = useViewCalendarYear();

  const handleDeleteItem = useCallback(() => {
    setSelectedFYPeriod((prev) => {
      const periods = cloneDeep(prev.periods);
      const index = periods?.findIndex((period) => period.rowId === rowId);

      if (index !== 0 && index !== periods.length - 1) {
        const newStartDate = periods[index - 1].endDate
          ? new Date(
              moment(periods[index - 1].endDate)
                .add(1, 'day')
                .format()
            )
          : null;
        periods[index + 1].startDate = newStartDate;
      }

      if (index === periods.length - 1) {
        periods[index - 1].endDate = periods[index].endDate;
      }
      if (index === 0) {
        periods[1].startDate = periods[0].startDate;
      }
      return {
        ...prev,
        periods: periods?.filter((period) => period.rowId !== rowId),
      };
    });
  }, [rowId, setSelectedFYPeriod]);

  const { messages, disabled } = useMemo(() => {
    const isPast = moment(dataItem.endDate).isBefore(moment());
    const hasOnlyOnePeriod = selectedFYPeriod.periods.length === 1;
    const disabled = dataItem.hasTransactionData || isPast || hasOnlyOnePeriod;

    const messages = hasOnlyOnePeriod
      ? ['Fiscal Year must have at least one period']
      : isPast
      ? ['Past periods cannot be deleted.']
      : dataItem.hasTransactionData
      ? ['This period contains committed expenses and cannot be deleted']
      : [];
    return { messages, disabled } as const;
  }, [dataItem.endDate, dataItem.hasTransactionData, selectedFYPeriod.periods]);

  return (
    <GridCellBase {...props} className="!text-center !p-0 relative">
      <div className="flex items-center flex-row justify-center absolute inset-0">
        <PopUp
          show={disabled}
          content={<PopUpContentWrapper type="Information" contents={messages} />}
        >
          <Button disabled={disabled} color="error" onClick={handleDeleteItem} size="sm">
            Delete
          </Button>
        </PopUp>
      </div>
    </GridCellBase>
  );
};

export default DeletePeriod;
